import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TabsContent } from '@/components/ui/tabs';
import apiClient from '@/utils/api';
import { toast } from 'sonner';
import { Heart, MapPin, IndianRupee, Home, Calendar, Briefcase, Save, Loader2 } from 'lucide-react';

const PROPERTY_TYPES = ['Apartment', 'Villa', 'Plot', 'Office', 'Shop', 'Commercial', 'Luxury Homes'];
const BUDGET_RANGES = [
  { value: 'under_50l', label: 'Under 50 Lakhs' },
  { value: '50l_1cr', label: '50 Lakhs - 1 Crore' },
  { value: '1cr_3cr', label: '1 - 3 Crore' },
  { value: '3cr_5cr', label: '3 - 5 Crore' },
  { value: '5cr_10cr', label: '5 - 10 Crore' },
  { value: 'above_10cr', label: 'Above 10 Crore' },
];
const CITIES = ['Hyderabad', 'Bangalore', 'Mumbai', 'Delhi', 'Chennai', 'Pune', 'Kolkata', 'Ahmedabad', 'Jaipur', 'Lucknow'];
const TIMELINES = [
  { value: 'immediately', label: 'Immediately' },
  { value: '3_months', label: 'Within 3 Months' },
  { value: '6_months', label: 'Within 6 Months' },
  { value: '1_year', label: 'Within 1 Year' },
  { value: 'exploring', label: 'Just Exploring' },
];

const PreferencesTab = () => {
  const [prefs, setPrefs] = useState({
    date_of_birth: '',
    age_range: '',
    marital_status: '',
    occupation: '',
    budget_range: '',
    home_purchase_timeline: '',
    preferred_cities: [],
    property_type_interest: [],
    investment_interest: false,
    rental_interest: false,
    vacation_home_interest: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchPrefs(); }, []);

  const fetchPrefs = async () => {
    try {
      const res = await apiClient.get('/account/preferences');
      if (res.data.preferences && Object.keys(res.data.preferences).length) {
        setPrefs(p => ({ ...p, ...res.data.preferences }));
      }
    } catch (e) { /* first time, no prefs */ }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put('/account/preferences', prefs);
      toast.success('Preferences saved successfully');
    } catch (e) {
      toast.error('Failed to save preferences');
    }
    setSaving(false);
  };

  const toggleArrayItem = (field, item) => {
    setPrefs(p => ({
      ...p,
      [field]: p[field].includes(item) ? p[field].filter(i => i !== item) : [...p[field], item]
    }));
  };

  if (loading) return <TabsContent value="preferences" className="mt-8"><div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div></TabsContent>;

  return (
    <TabsContent value="preferences" className="mt-8" data-testid="preferences-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl">My Preferences</h2>
        <Button onClick={handleSave} disabled={saving} className="rounded-none bg-[#15803D] hover:bg-[#166534]" data-testid="save-preferences-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          {saving ? 'Saving...' : 'Save Preferences'}
        </Button>
      </div>

      <div className="space-y-6">
        {/* Personal Info */}
        <div className="bg-white p-6">
          <div className="flex items-center gap-3 mb-4">
            <Calendar className="w-5 h-5 text-[#15803D]" />
            <h3 className="text-lg font-semibold">Personal Info</h3>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label>Date of Birth</Label>
              <Input type="date" className="mt-1" value={prefs.date_of_birth || ''} onChange={e => setPrefs({...prefs, date_of_birth: e.target.value})} data-testid="pref-dob" />
            </div>
            <div>
              <Label>Marital Status</Label>
              <select className="mt-1 w-full p-2 border rounded text-sm" value={prefs.marital_status || ''} onChange={e => setPrefs({...prefs, marital_status: e.target.value})} data-testid="pref-marital-status">
                <option value="">Select...</option>
                <option value="single">Single</option>
                <option value="married">Married</option>
                <option value="prefer_not_to_say">Prefer not to say</option>
              </select>
            </div>
            <div>
              <Label>Occupation</Label>
              <Input className="mt-1" value={prefs.occupation || ''} onChange={e => setPrefs({...prefs, occupation: e.target.value})} placeholder="e.g. Software Engineer" data-testid="pref-occupation" />
            </div>
          </div>
        </div>

        {/* Budget & Timeline */}
        <div className="bg-white p-6">
          <div className="flex items-center gap-3 mb-4">
            <IndianRupee className="w-5 h-5 text-[#15803D]" />
            <h3 className="text-lg font-semibold">Budget & Timeline</h3>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Budget Range</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {BUDGET_RANGES.map(b => (
                  <button key={b.value} type="button" onClick={() => setPrefs({...prefs, budget_range: prefs.budget_range === b.value ? '' : b.value})} className={`px-3 py-1.5 text-sm border rounded-full transition-colors ${prefs.budget_range === b.value ? 'bg-[#15803D] text-white border-[#15803D]' : 'hover:border-[#15803D]'}`} data-testid={`pref-budget-${b.value}`}>
                    {b.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Home Purchase Timeline</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {TIMELINES.map(t => (
                  <button key={t.value} type="button" onClick={() => setPrefs({...prefs, home_purchase_timeline: prefs.home_purchase_timeline === t.value ? '' : t.value})} className={`px-3 py-1.5 text-sm border rounded-full transition-colors ${prefs.home_purchase_timeline === t.value ? 'bg-[#15803D] text-white border-[#15803D]' : 'hover:border-[#15803D]'}`} data-testid={`pref-timeline-${t.value}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Preferred Cities */}
        <div className="bg-white p-6">
          <div className="flex items-center gap-3 mb-4">
            <MapPin className="w-5 h-5 text-[#15803D]" />
            <h3 className="text-lg font-semibold">Preferred Cities</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {CITIES.map(city => (
              <button key={city} type="button" onClick={() => toggleArrayItem('preferred_cities', city)} className={`px-3 py-1.5 text-sm border rounded-full transition-colors ${prefs.preferred_cities.includes(city) ? 'bg-[#15803D] text-white border-[#15803D]' : 'hover:border-[#15803D]'}`} data-testid={`pref-city-${city.toLowerCase()}`}>
                {city}
              </button>
            ))}
          </div>
        </div>

        {/* Property Types */}
        <div className="bg-white p-6">
          <div className="flex items-center gap-3 mb-4">
            <Home className="w-5 h-5 text-[#15803D]" />
            <h3 className="text-lg font-semibold">Property Type Interest</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {PROPERTY_TYPES.map(type => (
              <button key={type} type="button" onClick={() => toggleArrayItem('property_type_interest', type)} className={`px-3 py-1.5 text-sm border rounded-full transition-colors ${prefs.property_type_interest.includes(type) ? 'bg-[#15803D] text-white border-[#15803D]' : 'hover:border-[#15803D]'}`} data-testid={`pref-type-${type.toLowerCase().replace(/ /g, '-')}`}>
                {type}
              </button>
            ))}
          </div>
        </div>

        {/* Interest Toggles */}
        <div className="bg-white p-6">
          <div className="flex items-center gap-3 mb-4">
            <Heart className="w-5 h-5 text-[#15803D]" />
            <h3 className="text-lg font-semibold">Interest Areas</h3>
          </div>
          <div className="space-y-3">
            {[
              { key: 'investment_interest', label: 'Investment Properties', desc: 'Properties for investment and rental income' },
              { key: 'rental_interest', label: 'Rental Properties', desc: 'Looking for properties to rent' },
              { key: 'vacation_home_interest', label: 'Vacation Homes', desc: 'Holiday homes and weekend getaways' },
            ].map(item => (
              <label key={item.key} className="flex items-center justify-between p-3 border rounded cursor-pointer hover:bg-gray-50" data-testid={`pref-${item.key}`}>
                <div>
                  <p className="font-medium text-sm">{item.label}</p>
                  <p className="text-xs text-gray-500">{item.desc}</p>
                </div>
                <div className={`w-10 h-5 rounded-full transition-colors relative cursor-pointer ${prefs[item.key] ? 'bg-[#15803D]' : 'bg-gray-300'}`} onClick={() => setPrefs({...prefs, [item.key]: !prefs[item.key]})}>
                  <div className={`w-4 h-4 bg-white rounded-full absolute top-0.5 transition-transform ${prefs[item.key] ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </div>
              </label>
            ))}
          </div>
        </div>
      </div>
    </TabsContent>
  );
};

export default PreferencesTab;
